import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Gamepad2, Trophy, Users, Star, Play, Clock, Crown, Search,
  Zap, ChevronRight, Plus, DollarSign, TrendingUp, Settings,
  Grid, List, Filter, Heart, Lock, Unlock, X, Check, Medal, Swords, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GameArena = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [categories, setCategories] = useState([]);
  const [freeGames, setFreeGames] = useState([]);
  const [premiumGames, setPremiumGames] = useState([]);
  const [mySubscriptions, setMySubscriptions] = useState([]);
  const [publisherGames, setPublisherGames] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [matchStatus, setMatchStatus] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [myTournaments, setMyTournaments] = useState([]);
  const [showCreateTournament, setShowCreateTournament] = useState(false);
  const [tournamentFormats, setTournamentFormats] = useState([]);

  const getToken = () => localStorage.getItem('socyy_token');
  const isBusinessTier = () => {
    const tier = user?.subscription_tier;
    return ['enterprise', 'supremium', 'enterprise_supremium'].includes(tier) || user?.is_admin;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${getToken()}` };
      
      const [catRes, freeRes, premRes, subsRes] = await Promise.all([
        fetch(`${API}/api/gamearena/categories`),
        fetch(`${API}/api/gamearena/free`),
        fetch(`${API}/api/gamearena/premium`),
        fetch(`${API}/api/gamearena/my-subscriptions`, { headers })
      ]);
      
      const [catData, freeData, premData, subsData] = await Promise.all([
        catRes.json(), freeRes.json(), premRes.json(), subsRes.json()
      ]);
      
      setCategories(catData.categories || []);
      setFreeGames(freeData.games || []);
      setPremiumGames(premData.games || []);
      setMySubscriptions(subsData.subscriptions || []);
      
      // Fetch tournaments
      const [tourneysRes, formatsRes] = await Promise.all([
        fetch(`${API}/api/gamearena/tournaments?status=registration`, { headers }),
        fetch(`${API}/api/gamearena/tournaments/formats`, { headers })
      ]);
      const tourneysData = await tourneysRes.json();
      const formatsData = await formatsRes.json();
      setTournaments(tourneysData.tournaments || []);
      setTournamentFormats(formatsData.formats || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTournaments = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [allRes, myRes] = await Promise.all([
        fetch(`${API}/api/gamearena/tournaments`, { headers }),
        fetch(`${API}/api/gamearena/tournaments/my`, { headers })
      ]);
      const allData = await allRes.json();
      const myData = await myRes.json();
      setTournaments(allData.tournaments || []);
      setMyTournaments(myData.tournaments || []);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    }
  }, []);

  const fetchPublisherData = useCallback(async () => {
    if (!isBusinessTier()) return;
    try {
      const headers = { Authorization: `Bearer ${getToken()}` };
      const [gamesRes, earningsRes] = await Promise.all([
        fetch(`${API}/api/gamearena/publisher/games`, { headers }),
        fetch(`${API}/api/gamearena/publisher/earnings`, { headers })
      ]);
      
      const gamesData = await gamesRes.json();
      const earningsData = await earningsRes.json();
      
      setPublisherGames(gamesData.games || []);
      setEarnings(earningsData.earnings || null);
    } catch (err) {
      console.error('Error fetching publisher data:', err);
    }
  }, [user]);

  useEffect(() => {
    const userData = localStorage.getItem('socyy_user');
    if (userData) setUser(JSON.parse(userData));
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (user && isBusinessTier()) {
      fetchPublisherData();
    }
    if (user) {
      fetchTournaments();
    }
  }, [user, fetchPublisherData, fetchTournaments]);

  const joinMatchmaking = async (gameId) => {
    try {
      const res = await fetch(`${API}/api/gamearena/matchmaking/${gameId}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setMatchStatus(data);
        if (data.status === 'matched') {
          toast.success('Match found! Get ready to play!');
        } else {
          toast.info('Looking for opponents...');
        }
      } else {
        toast.error(data.detail || 'Failed to join matchmaking');
      }
    } catch (err) {
      toast.error('Error joining matchmaking');
    }
  };

  const subscribe = async (gameId) => {
    try {
      const res = await fetch(`${API}/api/gamearena/subscribe/${gameId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Subscribed! You can now play this game.');
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to subscribe');
      }
    } catch (err) {
      toast.error('Error subscribing');
    }
  };

  const cancelSub = async (subId) => {
    if (!window.confirm('Cancel this subscription? You can still play until the end of your billing period.')) return;
    
    try {
      const res = await fetch(`${API}/api/gamearena/subscriptions/${subId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success('Subscription cancelled');
        fetchData();
      }
    } catch (err) {
      toast.error('Error cancelling subscription');
    }
  };

  // Publish Game Modal
  const PublishGameModal = () => {
    const [formData, setFormData] = useState({
      name: '', description: '', category: 'arcade', icon: '🎮',
      subscription_price: 4.99, subscription_period: 'monthly',
      players: { min: 1, max: 1 }, difficulty: 'medium',
      game_url: '', instructions: '', tags: ''
    });
    const [publishing, setPublishing] = useState(false);

    const handlePublish = async (e) => {
      e.preventDefault();
      setPublishing(true);
      try {
        const payload = {
          ...formData,
          tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
        };
        
        const res = await fetch(`${API}/api/gamearena/publisher/games`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        
        if (data.success) {
          toast.success('Game submitted for review!');
          setShowPublishModal(false);
          fetchPublisherData();
        } else {
          toast.error(data.detail || 'Failed to publish');
        }
      } catch (err) {
        toast.error('Error publishing game');
      }
      setPublishing(false);
    };

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Gamepad2 className="w-6 h-6 text-purple-500" />
              Publish Your Game
            </h2>
            <button onClick={() => setShowPublishModal(false)} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handlePublish} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Game Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white resize-none"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Difficulty</label>
                <select
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Monthly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.99"
                  value={formData.subscription_price}
                  onChange={(e) => setFormData({ ...formData, subscription_price: parseFloat(e.target.value) })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Players</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    value={formData.players.min}
                    onChange={(e) => setFormData({ ...formData, players: { ...formData.players, min: parseInt(e.target.value) } })}
                    className="w-1/2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    min="1"
                    value={formData.players.max}
                    onChange={(e) => setFormData({ ...formData, players: { ...formData.players, max: parseInt(e.target.value) } })}
                    className="w-1/2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Game URL (iframe embed)</label>
              <input
                type="url"
                value={formData.game_url}
                onChange={(e) => setFormData({ ...formData, game_url: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                placeholder="https://yourgame.com/embed"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Tags (comma separated)</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                placeholder="action, multiplayer, puzzle"
              />
            </div>
            
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
              <p className="text-sm text-purple-300">
                <strong>Revenue Split:</strong> You receive 80% of subscription revenue. 
                Games are reviewed before going live.
              </p>
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowPublishModal(false)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={publishing || !formData.name}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg disabled:opacity-50"
              >
                {publishing ? 'Submitting...' : 'Submit for Review'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Game Card Component
  const GameCard = ({ game, showSubscribe = false }) => {
    const hasAccess = game.is_free || mySubscriptions.some(s => s.game_id === game.game_id);
    
    return (
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-all group">
        <div className="aspect-video bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center relative">
          <span className="text-5xl">{game.icon || '🎮'}</span>
          {!game.is_free && (
            <div className="absolute top-2 right-2">
              {hasAccess ? (
                <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                  <Unlock className="w-3 h-3" /> Subscribed
                </span>
              ) : (
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Premium
                </span>
              )}
            </div>
          )}
          {game.is_free && (
            <div className="absolute top-2 right-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">FREE</span>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="font-semibold text-white mb-1">{game.name}</h3>
          <p className="text-sm text-zinc-400 line-clamp-2 mb-3">{game.description}</p>
          
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {game.players?.min || 1}-{game.players?.max || 1}
            </span>
            <span className="capitalize">{game.difficulty || 'medium'}</span>
            {game.stats?.avg_rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-500" /> {game.stats.avg_rating}
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            {hasAccess ? (
              <button
                onClick={() => joinMatchmaking(game.game_id)}
                className="flex-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1"
              >
                <Play className="w-4 h-4" /> Play Now
              </button>
            ) : (
              <button
                onClick={() => subscribe(game.game_id)}
                className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1"
              >
                <Crown className="w-4 h-4" /> ${game.subscription_price}/mo
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Browse Tab
  const BrowseTab = () => (
    <div className="space-y-8">
      {/* Free Games Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-500" /> Free Games
          </h2>
          <span className="text-sm text-zinc-400">{freeGames.length} games</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {freeGames.map(game => (
            <GameCard key={game.game_id} game={game} />
          ))}
        </div>
      </section>

      {/* Premium Games Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Crown className="w-5 h-5 text-purple-500" /> Premium Games
          </h2>
          <span className="text-sm text-zinc-400">{premiumGames.length} games</span>
        </div>
        {premiumGames.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {premiumGames.map(game => (
              <GameCard key={game.game_id} game={game} showSubscribe />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <Crown className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No premium games yet</p>
            {isBusinessTier() && (
              <button
                onClick={() => setShowPublishModal(true)}
                className="mt-3 text-purple-500 hover:text-purple-400 text-sm"
              >
                Be the first to publish!
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );

  // My Games Tab
  const MyGamesTab = () => (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">My Subscriptions</h2>
      
      {mySubscriptions.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Gamepad2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">No subscriptions yet</p>
          <button
            onClick={() => setActiveTab('browse')}
            className="mt-3 text-purple-500 hover:text-purple-400 text-sm"
          >
            Browse premium games
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {mySubscriptions.map(sub => (
            <div key={sub.subscription_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-zinc-800 rounded-lg flex items-center justify-center text-2xl">
                  {sub.game?.icon || '🎮'}
                </div>
                <div>
                  <h3 className="font-semibold text-white">{sub.game?.name || 'Game'}</h3>
                  <p className="text-sm text-zinc-400">
                    ${sub.price}/{sub.period} • Ends {new Date(sub.ends_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => joinMatchmaking(sub.game_id)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm flex items-center gap-1"
                >
                  <Play className="w-4 h-4" /> Play
                </button>
                <button
                  onClick={() => cancelSub(sub.subscription_id)}
                  className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Publisher Tab
  const PublisherTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Publisher Dashboard</h2>
        <button
          onClick={() => setShowPublishModal(true)}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Publish Game
        </button>
      </div>

      {/* Earnings Card */}
      <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-800/50 rounded-xl p-6">
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-purple-300">Total Earnings</p>
            <p className="text-2xl font-bold text-white">${(earnings?.total_earnings || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-purple-300">Pending Payout</p>
            <p className="text-2xl font-bold text-white">${(earnings?.pending_payout || 0).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-purple-300">Total Paid</p>
            <p className="text-2xl font-bold text-white">${(earnings?.total_paid || 0).toFixed(2)}</p>
          </div>
        </div>
        <p className="text-xs text-purple-400 mt-4">Revenue split: 80% to you, 20% to Socyy • Min payout: $50</p>
      </div>

      {/* Published Games */}
      <div>
        <h3 className="font-semibold text-white mb-4">Your Games</h3>
        {publisherGames.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Gamepad2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">No games published yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {publisherGames.map(game => (
              <div key={game.game_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">{game.icon}</span>
                    <div>
                      <h4 className="font-semibold text-white">{game.name}</h4>
                      <div className="flex items-center gap-3 text-sm text-zinc-400">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          game.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                          game.status === 'pending_review' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {game.status.replace('_', ' ')}
                        </span>
                        <span>${game.subscription_price}/mo</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-400">{game.stats?.active_subscribers || 0} subscribers</p>
                    <p className="text-lg font-semibold text-emerald-500">${(game.stats?.total_revenue || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Tournaments Tab
  const TournamentsTab = () => {
    const joinTournament = async (tournamentId) => {
      try {
        const res = await fetch(`${API}/api/gamearena/tournaments/${tournamentId}/join`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Joined tournament!');
          fetchTournaments();
        } else {
          toast.error(data.detail || 'Failed to join');
        }
      } catch (err) {
        toast.error('Error joining tournament');
      }
    };

    const leaveTournament = async (tournamentId) => {
      try {
        const res = await fetch(`${API}/api/gamearena/tournaments/${tournamentId}/leave`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Left tournament');
          fetchTournaments();
        }
      } catch (err) {
        toast.error('Error leaving tournament');
      }
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" /> Tournaments
            </h2>
            <p className="text-sm text-zinc-400">Compete for prizes and glory!</p>
          </div>
          <button
            onClick={() => setShowCreateTournament(true)}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg flex items-center gap-2"
            data-testid="create-tournament-btn"
          >
            <Plus className="w-4 h-4" /> Create Tournament
          </button>
        </div>

        {/* My Tournaments */}
        {myTournaments.length > 0 && (
          <section>
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Medal className="w-4 h-4 text-yellow-500" /> My Tournaments
            </h3>
            <div className="space-y-3">
              {myTournaments.map(t => (
                <div key={t.tournament_id} className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-800/50 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-white flex items-center gap-2">
                        {t.name}
                        {t.is_creator && <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Creator</span>}
                      </h4>
                      <p className="text-sm text-zinc-400">
                        {t.current_participants}/{t.max_participants} players • {t.format?.replace('_', ' ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs ${
                        t.status === 'registration' ? 'bg-blue-500/20 text-blue-400' :
                        t.status === 'in_progress' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {t.status.replace('_', ' ')}
                      </span>
                      {t.entry_fee > 0 && (
                        <p className="text-sm text-yellow-400 mt-1">${t.prize_pool} prize pool</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Open Tournaments */}
        <section>
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Swords className="w-4 h-4 text-purple-500" /> Open for Registration
          </h3>
          {tournaments.filter(t => t.status === 'registration').length === 0 ? (
            <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
              <Trophy className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">No open tournaments</p>
              <button
                onClick={() => setShowCreateTournament(true)}
                className="mt-3 text-yellow-500 hover:text-yellow-400 text-sm"
              >
                Create the first one!
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {tournaments.filter(t => t.status === 'registration').map(t => {
                const isRegistered = t.participants?.includes(user?.user_id);
                return (
                  <div key={t.tournament_id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-white">{t.name}</h4>
                        <p className="text-sm text-zinc-400 capitalize">{t.format?.replace('_', ' ')}</p>
                      </div>
                      <Trophy className="w-5 h-5 text-yellow-500" />
                    </div>
                    
                    <div className="space-y-2 text-sm mb-4">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Players</span>
                        <span className="text-white">{t.current_participants}/{t.max_participants}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Entry Fee</span>
                        <span className="text-white">{t.entry_fee > 0 ? `$${t.entry_fee}` : 'Free'}</span>
                      </div>
                      {t.prize_pool > 0 && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Prize Pool</span>
                          <span className="text-yellow-400 font-semibold">${t.prize_pool}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Progress bar */}
                    <div className="h-2 bg-zinc-800 rounded-full mb-4">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full transition-all"
                        style={{ width: `${(t.current_participants / t.max_participants) * 100}%` }}
                      />
                    </div>
                    
                    {isRegistered ? (
                      <button
                        onClick={() => leaveTournament(t.tournament_id)}
                        className="w-full px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm"
                      >
                        Leave Tournament
                      </button>
                    ) : (
                      <button
                        onClick={() => joinTournament(t.tournament_id)}
                        disabled={t.current_participants >= t.max_participants}
                        className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg text-sm disabled:opacity-50"
                      >
                        {t.entry_fee > 0 ? `Join ($${t.entry_fee})` : 'Join Free'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    );
  };

  // Create Tournament Modal
  const CreateTournamentModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      game_id: freeGames[0]?.game_id || '',
      format: 'single_elimination',
      entry_fee: 0,
      max_participants: 8,
      rules: ''
    });
    const [creating, setCreating] = useState(false);

    const handleCreate = async (e) => {
      e.preventDefault();
      setCreating(true);
      try {
        const res = await fetch(`${API}/api/gamearena/tournaments`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify(formData)
        });
        const data = await res.json();
        if (data.success) {
          toast.success('Tournament created!');
          setShowCreateTournament(false);
          fetchTournaments();
        } else {
          toast.error(data.detail || 'Failed to create');
        }
      } catch (err) {
        toast.error('Error creating tournament');
      }
      setCreating(false);
    };

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-zinc-900 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" /> Create Tournament
            </h2>
            <button onClick={() => setShowCreateTournament(false)} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleCreate} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Tournament Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                placeholder="Sunday Showdown"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Game</label>
              <select
                value={formData.game_id}
                onChange={(e) => setFormData({ ...formData, game_id: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              >
                {freeGames.map(g => (
                  <option key={g.game_id} value={g.game_id}>{g.icon} {g.name}</option>
                ))}
                {premiumGames.map(g => (
                  <option key={g.game_id} value={g.game_id}>{g.icon} {g.name} (Premium)</option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Format</label>
                <select
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  {tournamentFormats.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-1">Max Players</label>
                <select
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
                >
                  {[4, 8, 16, 32, 64].map(n => (
                    <option key={n} value={n}>{n} players</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Entry Fee ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.entry_fee}
                onChange={(e) => setFormData({ ...formData, entry_fee: parseFloat(e.target.value) || 0 })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">Set to 0 for free tournaments</p>
            </div>
            
            {formData.entry_fee > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-yellow-300">
                  <strong>Prize Pool:</strong> ${(formData.entry_fee * formData.max_participants * 0.8).toFixed(2)}
                </p>
                <p className="text-xs text-yellow-400 mt-1">
                  Distribution: 60% 1st, 30% 2nd, 10% 3rd (20% platform fee)
                </p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1">Rules (optional)</label>
              <textarea
                value={formData.rules}
                onChange={(e) => setFormData({ ...formData, rules: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white resize-none"
                rows={2}
                placeholder="Tournament rules..."
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowCreateTournament(false)}
                className="flex-1 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating || !formData.name || !formData.game_id}
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Tournament'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'browse', label: 'Browse Games', icon: Grid },
    { id: 'tournaments', label: 'Tournaments', icon: Trophy },
    { id: 'mygames', label: 'My Games', icon: Gamepad2 },
    ...(isBusinessTier() ? [{ id: 'publisher', label: 'Publisher', icon: Crown }] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="gamearena-page">
      {/* Header */}
      <header className="bg-zinc-950 border-b border-zinc-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/feed')} className="text-zinc-400 hover:text-white">←</button>
            <div className="flex items-center gap-2">
              <Gamepad2 className="w-6 h-6 text-purple-500" />
              <h1 className="text-xl font-bold text-white">GameArena</h1>
            </div>
          </div>
          
          {/* Match Status */}
          {matchStatus?.status === 'waiting' && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
              <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
              Finding match...
            </div>
          )}
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-zinc-950 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto py-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'browse' && <BrowseTab />}
        {activeTab === 'tournaments' && <TournamentsTab />}
        {activeTab === 'mygames' && <MyGamesTab />}
        {activeTab === 'publisher' && <PublisherTab />}
      </main>

      {/* Modals */}
      {showPublishModal && <PublishGameModal />}
      {showCreateTournament && <CreateTournamentModal />}
    </div>
  );
};

export default GameArena;
